/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Keycloak authentication provider
 *
 * Adapts the Keycloak JavaScript client to the provider-neutral authentication
 * contract and confines Keycloak-specific session, refresh, and redirect APIs.
 */

import {inject, Injectable} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEvent,
  KeycloakEventType,
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import {
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  ReplaySubject,
  shareReplay,
  startWith,
} from 'rxjs';

import {
  IAuthenticatedUser,
  IAuthenticationError,
  IAuthenticationLoginOptions,
  IAuthenticationLogoutOptions,
  IAuthenticationProvider,
  LOGIN_REQUIRED_ERROR_CODE,
} from '../authentication-provider.token';
import {DEFAULT_LOGOUT_RETURN_URL, resolveReturnUrl} from '../return-url.util';

/**
 * Remaining access-token validity, in seconds, below which `keycloak-js`
 * refreshes the token before it is handed to a backend request.
 */
const TOKEN_MIN_VALIDITY_SECONDS = 60;

/**
 * `keycloak-js` sentinel value that forces a token refresh regardless of the
 * remaining validity of the current access token.
 */
const FORCE_TOKEN_REFRESH_MIN_VALIDITY_SECONDS = -1;

/** Fallback message when Keycloak provides no usable error description. */
const DEFAULT_ERROR_MESSAGE = 'Keycloak authentication failed.';

/** Message used when a token is requested without a valid Keycloak session. */
const SESSION_REQUIRED_MESSAGE =
  'The Keycloak session is missing or expired. Please sign in again.';

/**
 * Keycloak implementation of the provider-neutral webapp authentication contract.
 *
 * The service reads the Keycloak client and event signal registered by
 * `provideKeycloakAuthentication()`. No Keycloak state is evaluated while the
 * service is constructed; all observables derive their values lazily from the
 * event signal after the adapter initialization has completed.
 */
@Injectable({providedIn: 'root'})
export class KeycloakAuthenticationProviderService implements IAuthenticationProvider {
  private readonly keycloak = inject(Keycloak);
  private readonly operationErrors = new ReplaySubject<IAuthenticationError>(1);
  private readonly keycloakEvents$: Observable<KeycloakEvent> = toObservable(
    inject(KEYCLOAK_EVENT_SIGNAL),
  ).pipe(shareReplay({bufferSize: 1, refCount: true}));

  readonly isAuthenticated$: Observable<boolean> = this.keycloakEvents$.pipe(
    map(() => this.isLoggedIn()),
    distinctUntilChanged(),
  );
  readonly user$: Observable<IAuthenticatedUser | null> =
    this.keycloakEvents$.pipe(
      map(() => this.toAuthenticatedUser()),
      distinctUntilChanged(
        (previousUser, currentUser) =>
          previousUser?.sub === currentUser?.sub &&
          previousUser?.name === currentUser?.name &&
          previousUser?.email === currentUser?.email,
      ),
    );
  /**
   * Latest normalized authentication error.
   *
   * Token refresh failures are reported once through `getAccessToken()`; the
   * corresponding `AuthRefreshError` event is intentionally not mapped again.
   */
  readonly error$: Observable<IAuthenticationError | null> = merge(
    this.operationErrors,
    this.keycloakEvents$.pipe(
      filter((event) => event.type === KeycloakEventType.AuthError),
      map((event) => this.toAuthenticationError(event.args)),
    ),
  ).pipe(startWith(null));

  /**
   * Refreshes a Keycloak token when needed and returns the resulting access token.
   *
   * A failure while no Keycloak session exists (never authenticated, or the
   * refresh token was rejected and the session was cleared) is reported with
   * the {@link LOGIN_REQUIRED_ERROR_CODE} so the application can restart login.
   *
   * @param forceRefresh - Whether the current access token must be refreshed.
   * @returns A promise resolving to a valid Keycloak access token.
   */
  async getAccessToken(forceRefresh = false): Promise<string> {
    try {
      await this.keycloak.updateToken(
        forceRefresh
          ? FORCE_TOKEN_REFRESH_MIN_VALIDITY_SECONDS
          : TOKEN_MIN_VALIDITY_SECONDS,
      );

      return this.requireAccessToken();
    } catch (error: unknown) {
      const authenticationError = this.toTokenRetrievalError(error);
      this.operationErrors.next(authenticationError);
      throw error instanceof Error
        ? error
        : new Error(authenticationError.message);
    }
  }

  /**
   * Starts a Keycloak login or registration redirect.
   *
   * @param options - Login intent and optional post-login route.
   * @returns A promise that resolves after Keycloak accepts the redirect.
   */
  async login(options?: IAuthenticationLoginOptions): Promise<void> {
    return this.captureAuthenticationError(async () => {
      const redirectUri = resolveReturnUrl(options?.returnUrl);

      if (options?.registration) {
        await this.keycloak.register({redirectUri});
        return;
      }

      await this.keycloak.login({redirectUri});
    });
  }

  /**
   * Ends the Keycloak browser session and redirects to the requested route.
   *
   * @param options - Optional application-relative post-logout route.
   * @returns A promise that resolves after Keycloak accepts the logout redirect.
   */
  async logout(options?: IAuthenticationLogoutOptions): Promise<void> {
    return this.captureAuthenticationError(async () => {
      await this.keycloak.logout({
        redirectUri: resolveReturnUrl(
          options?.returnUrl,
          DEFAULT_LOGOUT_RETURN_URL,
        ),
      });
    });
  }

  /**
   * Determines whether the Keycloak adapter currently holds an authenticated session.
   *
   * @returns `true` only after Keycloak reported a successful authentication.
   */
  private isLoggedIn(): boolean {
    return this.keycloak.authenticated === true;
  }

  /**
   * Returns the current access token or fails when the session holds none.
   *
   * @returns The current Keycloak access token.
   * @throws Error when Keycloak has no access token after a refresh attempt.
   */
  private requireAccessToken(): string {
    const token = this.keycloak.token;

    if (!token) {
      throw new Error(SESSION_REQUIRED_MESSAGE);
    }

    return token;
  }

  /**
   * Converts the current Keycloak token claims to the application user model.
   *
   * @returns The authenticated user, or `null` when Keycloak has no OIDC subject.
   */
  private toAuthenticatedUser(): IAuthenticatedUser | null {
    const token = this.keycloak.tokenParsed;

    if (!this.isLoggedIn() || !token?.sub) {
      return null;
    }

    return {
      sub: token.sub,
      ...(typeof token['name'] === 'string' ? {name: token['name']} : {}),
      ...(typeof token['email'] === 'string' ? {email: token['email']} : {}),
    };
  }

  /**
   * Normalizes a token retrieval failure, marking missing sessions as login required.
   *
   * `keycloak-js` rejects `updateToken()` without an error object when no
   * refresh token exists and clears the session when the refresh token is
   * rejected, so the session state decides whether a new login is required.
   *
   * @param error - The value rejected by the Keycloak token refresh.
   * @returns A normalized authentication error for application-level handling.
   */
  private toTokenRetrievalError(error: unknown): IAuthenticationError {
    if (!this.isLoggedIn()) {
      return {
        code: LOGIN_REQUIRED_ERROR_CODE,
        message: SESSION_REQUIRED_MESSAGE,
      };
    }

    return this.toAuthenticationError(error);
  }

  /**
   * Converts Keycloak and JavaScript errors to the provider-neutral error model.
   *
   * @param error - The error emitted or thrown by Keycloak.
   * @returns A normalized authentication error for application-level UI.
   */
  private toAuthenticationError(error: unknown): IAuthenticationError {
    if (typeof error === 'object' && error !== null) {
      const keycloakError = error as {
        error?: string;
        errorMessage?: string;
        message?: string;
      };
      const message =
        keycloakError.errorMessage ??
        keycloakError.message ??
        keycloakError.error ??
        DEFAULT_ERROR_MESSAGE;

      return {
        message,
        ...(keycloakError.error ? {code: keycloakError.error} : {}),
      };
    }

    return {message: DEFAULT_ERROR_MESSAGE};
  }

  /**
   * Emits a normalized error before propagating an SDK operation failure.
   *
   * @typeParam TResult - The successful operation result type.
   * @param operation - The Keycloak operation to execute.
   * @returns The successful operation result.
   */
  private async captureAuthenticationError<TResult>(
    operation: () => Promise<TResult>,
  ): Promise<TResult> {
    try {
      return await operation();
    } catch (error: unknown) {
      this.operationErrors.next(this.toAuthenticationError(error));
      throw error;
    }
  }
}
